import type { PeopleRow, ServerMemberRow } from '../../../services/api'

/**
 * Map a ServerMemberRow (server-admin payload) onto the PeopleRow
 * shape consumed by the shared PeopleEditModal. The two are 95%
 * the same — only ride-along fields differ (ServerMemberRow carries
 * `nickname`, `joinedAt`, `roles`, `departmentId`; PeopleRow carries
 * `orgChartEmpId`, `departmentName`).
 *
 * `departmentName` is forced to null (the column doesn't exist on
 * `User`; see routes/people.ts /directory note).
 */
export function serverMemberToPeopleRow(m: ServerMemberRow): PeopleRow {
  const u = m.user!
  return {
    id: u.id,
    email: u.email,
    firstName: u.firstName,
    lastName: u.lastName,
    position: u.position,
    avatarUrl: u.avatarUrl,
    accountStatus: u.accountStatus,
    cellPhone: u.cellPhone,
    locationCountry: u.locationCountry,
    departmentName: null,
    orgChartEmpId: null,
    inviteSentAt: u.inviteSentAt,
    hireDate: u.hireDate,
    birthday: null,
    // Pass null through — never fall back to `now()`, that misleads
    // the modal into showing "just now" for accounts that never
    // signed in. The MetaRow formatter renders null as '—' / "Never".
    lastSeenAt: u.lastSeenAt,
    // Carry the real org fields so the shared PeopleEditModal doesn't null
    // them on save. Forcing these to null let a Server-Admin edit detach a
    // person from their manager (rendered at the company root, beside the
    // CEO) and wipe their bio. The members.page payload now includes both.
    aboutMe: u.aboutMe,
    managerId: u.managerId,
  }
}

/**
 * Pass-through used when the PATCH route already returns a
 * PeopleRow-compatible shape. Kept symmetric with
 * `serverMemberToPeopleRow` so the call site reads naturally.
 */
export function peopleRowFromServerMember(row: PeopleRow): PeopleRow {
  return row
}

/**
 * OSPROD-275: every department a member belongs to, primary first.
 *
 * Mirrors the server helper of the same name. Tolerates rows that predate the
 * `departmentIds` column (or a payload that only carried `departmentId`) by
 * falling back to the primary alone — such a member must never render as
 * "no departments" when they clearly have one.
 */
export const MAX_MEMBER_DEPARTMENTS = 3

export function memberDepartments(
  m: { departmentId?: string | null; departmentIds?: unknown } | null | undefined,
): string[] {
  if (!m) return []
  const out: string[] = []
  const push = (v: unknown) => { if (typeof v === 'string' && v && !out.includes(v)) out.push(v) }
  push(m.departmentId)
  if (Array.isArray(m.departmentIds)) for (const v of m.departmentIds) push(v)
  return out
}
